import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '@/core/api/client';
import { getEmail, getToken, setEmail, setToken } from '@/lib/storage';
import { LoadingView, showToast } from '@/components/ui';

const AuthPage = lazy(() => import('@/features/auth/AuthPage').then((module) => ({ default: module.AuthPage })));
const ShellPage = lazy(() => import('@/features/shell/ShellPage').then((module) => ({ default: module.ShellPage })));
const HomePage = lazy(() => import('@/features/home/HomePage').then((module) => ({ default: module.HomePage })));
const DataPackPage = lazy(() => import('@/features/data-pack/DataPackPage').then((module) => ({ default: module.DataPackPage })));
const VoucherPage = lazy(() => import('@/features/voucher/VoucherPage').then((module) => ({ default: module.VoucherPage })));
const PackDetailPage = lazy(() => import('@/features/data-pack/PackDetailPage').then((module) => ({ default: module.PackDetailPage })));
const TopupPage = lazy(() => import('@/features/topup/TopupPage').then((module) => ({ default: module.TopupPage })));
const HistoryPage = lazy(() => import('@/features/history/HistoryPage').then((module) => ({ default: module.HistoryPage })));
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const MlCheckerPage = lazy(() => import('@/features/ml-checker/MlCheckerPage').then((module) => ({ default: module.MlCheckerPage })));
const PrivacyPolicyPage = lazy(() => import('@/features/privacy-policy/PrivacyPolicyPage').then((module) => ({ default: module.PrivacyPolicyPage })));
const GameDetailPage = lazy(() => import('@/features/game-detail/GameDetailPage').then((module) => ({ default: module.GameDetailPage })));
const PurchaseHistoryView = lazy(() => import('@/features/history/PurchaseHistoryView').then((module) => ({ default: module.PurchaseHistoryView })));
const TopupHistoryView = lazy(() => import('@/features/history/TopupHistoryView').then((module) => ({ default: module.TopupHistoryView })));
const ContactAdminPage = lazy(() => import('@/features/contact-admin/ContactAdminPage').then((module) => ({ default: module.ContactAdminPage })));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage').then((module) => ({ default: module.NotificationsPage })));
const SpinPage = lazy(() => import('@/features/spin/SpinPage').then((module) => ({ default: module.SpinPage })));
const DeviceManagementPage = lazy(() => import('@/features/devices/DeviceManagementPage').then((module) => ({ default: module.DeviceManagementPage })));
const LeaderboardPage = lazy(() => import('@/features/leaderboard/LeaderboardPage').then((module) => ({ default: module.LeaderboardPage })));
const ChatPage = lazy(() => import('@/features/chat/ChatPage').then((module) => ({ default: module.ChatPage })));
const EventPage = lazy(() => import('@/features/event/EventPage').then((module) => ({ default: module.EventPage })));
const UserGuidePage = lazy(() => import('@/features/user-guide/UserGuidePage').then((module) => ({ default: module.UserGuidePage })));

function RouteFallback() {
  return (
    <div className="content" style={{ minHeight: '40vh', display: 'grid', placeItems: 'center' }}>
      <LoadingView size={84} text="" />
    </div>
  );
}

export function AppRouter() {
  const [token, setTokenState] = useState<string | null>(() => {
    const savedToken = getToken();
    if (savedToken) apiClient.setToken(savedToken);
    return savedToken;
  });
  const [email, setEmailState] = useState<string | null>(() => getEmail());
  const [userName, setUserName] = useState<string | null>(null);
  const [coins, setCoins] = useState<number | null>(null);

  const session = useMemo(() => ({ token, email }), [token, email]);
  const isLoggedIn = !!token;

  const bootstrap = async () => {
    const savedToken = getToken();
    const savedEmail = getEmail();
    if (savedToken) {
      apiClient.setToken(savedToken);
      setTokenState(savedToken);
    }
    if (savedEmail) {
      setEmailState(savedEmail);
    }
    try {
      const calls: Promise<unknown>[] = [];
      if (savedToken) {
        calls.push(apiClient.fetchUser().then((u) => {
          setUserName(u.name);
          setCoins(u.coins);
          setEmailState(u.email ?? savedEmail ?? null);
        }));
      }
      await Promise.all(calls);
    } catch {
      // Startup prefetch should not block routing.
    }
  };

  useEffect(() => {
    void bootstrap();
  }, []);

  useEffect(() => {
    const handleInvalidToken = () => {
      setSession(null, null);
      showToast('Invalid token. Please log in again.', 'error', 'Session expired');
    };

    window.addEventListener('amt-auth-invalid', handleInvalidToken);
    return () => window.removeEventListener('amt-auth-invalid', handleInvalidToken);
  }, []);

  const setSession = (nextToken: string | null, nextEmail: string | null) => {
    if (nextToken == null) {
      setToken(null);
      setEmail(null);
      setTokenState(null);
      setEmailState(null);
      apiClient.clearToken();
      return;
    }
    setToken(nextToken);
    setEmail(nextEmail);
    setTokenState(nextToken);
    setEmailState(nextEmail);
    apiClient.setToken(nextToken);
    if (nextEmail) setEmail(nextEmail);
  };

  const handleLoginSuccess = (nextToken: string | null, nextEmail: string | null) => {
    setSession(nextToken, nextEmail);
    // After login, redirect to home page
  };

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Auth Page - Full screen, no shell */}
        <Route path="/auth" element={<AuthPage onLoggedIn={handleLoginSuccess} />} />
        

        {/* All other pages with Shell (drawer + appbar) */}
        <Route element={
          <ShellPage 
            session={session} 
            isLoggedIn={isLoggedIn} 
            onLogout={() => setSession(null, null)} 
            onSessionUpdate={setSession}
          />
        }>
          {/* Home - Public */}
          <Route path="/" element={<HomePage />} />
          
          {/* Data & Pack - Public */}
          <Route path="/data-pack" element={<DataPackPage />} />
          <Route path="/data-pack/:providerId" element={<PackDetailPage />} />
          <Route path="/voucher" element={<VoucherPage />} />
          <Route path="/voucher/vouchers" element={<VoucherPage />} />
          <Route path="/voucher/premium" element={<Navigate to="/voucher" replace />} />
          <Route path="/voucher/p/:id" element={<Navigate to="/voucher" replace />} />
          <Route path="/voucher/item/:productId" element={<Navigate to="/voucher" replace />} />
          <Route path="/voucher/v/:id" element={<VoucherPage />} />
          
          {/* Protected Routes - Require Login */}
          <Route element={<ProtectedRoute isLoggedIn={isLoggedIn} />}>
            <Route path="/topup" element={<TopupPage email={email} coins={coins} />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/history/orders" element={<div className="content"><PurchaseHistoryView /></div>} />
            <Route path="/history/topups" element={<div className="content"><TopupHistoryView /></div>} />
            <Route path="/profile" element={<ProfilePage userName={userName} email={email} coins={coins} onLogout={() => setSession(null, null)} onProfileUpdated={(n, e) => { if (n) setUserName(n); if (e) { setEmailState(e); setEmail(e); setSession(token, e); } }} />} />
            <Route path="/game/:gameKey" element={<GameDetailPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/spin" element={<SpinPage />} />
            <Route path="/devices" element={<DeviceManagementPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/chat" element={<ChatPage />} />
          </Route>
          
          {/* Public Routes */}
          <Route path="/ml-checker" element={<MlCheckerPage />} />
          <Route path="/contact-admin" element={<ContactAdminPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/user-guide" element={<UserGuidePage />} />
          <Route path="/event" element={<EventPage />} />
        </Route>
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

// Protected Route wrapper - redirects to auth if not logged in
function ProtectedRoute({ isLoggedIn }: { isLoggedIn: boolean }) {
  const location = useLocation();
  
  if (!isLoggedIn) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }
  
  return <Outlet />;
}

export function useGoBackOr(path: string) {
  const navigate = useNavigate();
  return () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(path);
  };
}
